import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from "react";
import { useAuth } from "./AuthContext";
import { UserProgress } from "../modules/academy/progression.types";
import { allThemes } from "../modules/themes";
import { allModules } from "../modules/academy/models";
import { ThemeModule } from "../modules/themes/theme.types";

type ProgressionContextValue = {
  progress: UserProgress | null;
  isGuest: boolean;
  markMicroCourseCompleted: (microCourseId: string, themeId: string, moduleIds?: string[]) => void;
  setInProgressMicroCourse: (microCourseId: string | null) => void;
  setQuizScore: (quizId: string, themeId: string, score: number) => void;
  recomputeThemeProgress: (themeId: string) => void;
};

const ProgressionContext = createContext<ProgressionContextValue | undefined>(undefined);

const STORAGE_KEY = "mixmind_academy_progress_v1";

const defaultProgress: UserProgress = {
  completedMicroCourses: [],
  completedModules: [],
  quizScores: {},
  themeProgress: {},
  startedThemes: [],
  inProgressMicroCourseId: null,
  parcoursProgress: {},
};

function uniquePush(list: string[], id: string): string[] {
  return list.includes(id) ? list : [...list, id];
}

function getThemeById(id: string): ThemeModule | undefined {
  return allThemes.find((t) => t.id === id);
}

function getModulesForTheme(themeId: string) {
  const t = getThemeById(themeId);
  if (!t || !t.moduleIds) return [];
  return t.moduleIds
    .map((mid) => allModules.find((m) => m.id === mid))
    .filter((m): m is typeof allModules[number] => Boolean(m));
}

function computeThemeProgress(themeId: string, progress: UserProgress): number {
  const modules = getModulesForTheme(themeId);
  if (!modules || modules.length === 0) return 0;
  const modulePercents = modules.map((mod) => {
    const total = mod.microCourseIds.length;
    if (total === 0) return 0;
    const done = mod.microCourseIds.filter((id) => progress.completedMicroCourses.includes(id)).length;
    return Math.floor((done / total) * 100);
  });
  const avg = modulePercents.reduce((acc, v) => acc + v, 0) / modulePercents.length;
  return Math.round(avg);
}

export function ProgressionProvider({ children }: { children: React.ReactNode }) {
  const { isAuthed } = useAuth();
  const isGuest = !isAuthed;
  const [progress, setProgress] = useState<UserProgress | null>(null);

  useEffect(() => {
    if (isGuest) {
      setProgress(null);
      return;
    }
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as UserProgress;
        setProgress({
          ...defaultProgress,
          ...parsed,
          parcoursProgress: parsed.parcoursProgress || {},
        });
      } else {
        setProgress({ ...defaultProgress });
      }
    } catch (e) {
      console.error("Failed to parse progress", e);
      setProgress({ ...defaultProgress });
    }
  }, [isGuest]);

  useEffect(() => {
    if (isGuest || !progress) return;
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(progress));
    } catch (e) {
      console.error("Failed to persist progress", e);
    }
  }, [progress, isGuest]);

  const markMicroCourseCompleted = useCallback(
    (microCourseId: string, themeId: string, moduleIds?: string[]) => {
      if (isGuest) return;
      setProgress((prev) => {
        const base = prev || { ...defaultProgress };
        const nextCompleted = uniquePush(base.completedMicroCourses, microCourseId);

        const targetModules =
          moduleIds && moduleIds.length > 0
            ? moduleIds
                .map((id) => allModules.find((m) => m.id === id))
                .filter((m): m is typeof allModules[number] => Boolean(m))
            : allModules.filter((m) => m.microCourseIds.includes(microCourseId));

        const completedModulesSet = new Set(base.completedModules);
        targetModules.forEach((mod) => {
          const total = mod.microCourseIds.length;
          const done = mod.microCourseIds.filter((id) => nextCompleted.includes(id)).length;
          if (total > 0 && done === total) {
            completedModulesSet.add(mod.id);
          }
        });

        const next: UserProgress = {
          ...base,
          completedMicroCourses: nextCompleted,
          completedModules: Array.from(completedModulesSet),
          startedThemes: uniquePush(base.startedThemes, themeId),
          inProgressMicroCourseId:
            base.inProgressMicroCourseId === microCourseId ? null : base.inProgressMicroCourseId,
          themeProgress: {
            ...base.themeProgress,
            [themeId]: computeThemeProgress(themeId, {
              ...base,
              completedMicroCourses: nextCompleted,
              completedModules: Array.from(completedModulesSet),
              parcoursProgress: base.parcoursProgress || {},
            }),
          },
          parcoursProgress: base.parcoursProgress || {},
        };
        return next;
      });
    },
    [isGuest]
  );

  const setInProgressMicroCourse = useCallback(
    (microCourseId: string | null) => {
      if (isGuest) return;
      setProgress((prev) => {
        const base = prev || { ...defaultProgress };
        return { ...base, inProgressMicroCourseId: microCourseId };
      });
    },
    [isGuest]
  );

  const setQuizScore = useCallback(
    (quizId: string, themeId: string, score: number) => {
      if (isGuest) return;
      setProgress((prev) => {
        const base = prev || { ...defaultProgress };
        const prevScore = base.quizScores[quizId] ?? 0;
        const best = Math.max(prevScore, score);
        const next: UserProgress = {
          ...base,
          quizScores: { ...base.quizScores, [quizId]: best },
          startedThemes: uniquePush(base.startedThemes, themeId),
          themeProgress: {
            ...base.themeProgress,
            [themeId]: computeThemeProgress(themeId, base),
          },
          parcoursProgress: base.parcoursProgress || {},
        };
        return next;
      });
    },
    [isGuest]
  );

  const recomputeThemeProgress = useCallback((themeId: string) => {
    if (isGuest) return;
    setProgress((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        themeProgress: {
          ...prev.themeProgress,
          [themeId]: computeThemeProgress(themeId, prev),
        },
      };
    });
  }, [isGuest]);

  const value = useMemo<ProgressionContextValue>(
    () => ({
      progress,
      isGuest,
      markMicroCourseCompleted,
      setInProgressMicroCourse,
      setQuizScore,
      recomputeThemeProgress,
    }),
    [progress, isGuest, markMicroCourseCompleted, setInProgressMicroCourse, setQuizScore, recomputeThemeProgress]
  );

  return <ProgressionContext.Provider value={value}>{children}</ProgressionContext.Provider>;
}

export function useProgression() {
  const ctx = useContext(ProgressionContext);
  if (!ctx) throw new Error("useProgression must be used within ProgressionProvider");
  return ctx;
}
